import { Button, EmptyState, Skeleton } from '@estac1/ui';
import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import { ClipboardList } from 'lucide-react';
import { AuditDesktopTable } from './audit-desktop-table.js';
import { AuditMobileList } from './audit-mobile-list.js';
import type { AuditEntry, AuditResponse } from './types.js';

interface AuditEntriesProps {
  audit: UseInfiniteQueryResult<InfiniteData<AuditResponse>, Error>;
  entries: AuditEntry[];
  expanded: Set<string>;
  onToggle: (id: string) => void;
  hasActiveFilter: boolean;
}

export const AuditEntries = ({
  audit,
  entries,
  expanded,
  onToggle,
  hasActiveFilter,
}: AuditEntriesProps) => {
  if (audit.isLoading) {
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (audit.isError) {
    return (
      <div className="p-6">
        <EmptyState
          icon={<ClipboardList className="h-5 w-5" />}
          title="Não foi possível carregar"
          description="Verifique sua conexão e tente novamente em instantes."
        />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
          icon={<ClipboardList className="h-5 w-5" />}
          title={hasActiveFilter ? 'Nenhum registro com esses filtros' : 'Sem registros'}
          description={
            hasActiveFilter
              ? 'Ajuste os filtros acima ou limpe para ver tudo.'
              : 'Quando algum admin executar uma ação, aparece aqui.'
          }
        />
      </div>
    );
  }

  return (
    <>
      <AuditMobileList entries={entries} expanded={expanded} onToggle={onToggle} />
      <AuditDesktopTable entries={entries} expanded={expanded} onToggle={onToggle} />
      {audit.hasNextPage && (
        <div className="flex justify-center p-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => audit.fetchNextPage()}
            disabled={audit.isFetchingNextPage}
          >
            {audit.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
          </Button>
        </div>
      )}
    </>
  );
};
